import type { ComponentType, ReactNode } from 'react'
import { Briefcase } from 'lucide-react'

import { AuthLogoImage } from '@/components/auth/auth-logo-image'
import { buildAuthThemeStyle } from '@/lib/auth-theme'

type TenantAuthShellProps = {
  organizationName: string
  organizationLogo?: string | null
  sidePanelLogo?: string | null
  welcomeLogo?: string | null
  sidePanelLogos?: Array<string | null | undefined>
  welcomeLogos?: Array<string | null | undefined>
  primaryColor?: string | null
  icon?: ComponentType<{ className?: string }>
  eyebrow?: string
  title: string
  description: ReactNode
  panelTitle: string
  panelDescription: ReactNode
  sideActions?: ReactNode
  children: ReactNode
  contentMaxWidth?: string
  logoPosition?: 'left' | 'right'
  contentVerticalAlign?: 'start' | 'center'
  contentPanelClassName?: string
  rightLogoContainerClassName?: string
}

export function TenantAuthShell({
  organizationName,
  organizationLogo,
  sidePanelLogo,
  welcomeLogo,
  sidePanelLogos,
  welcomeLogos,
  primaryColor,
  icon: Icon = Briefcase,
  eyebrow,
  title,
  description,
  panelTitle,
  panelDescription,
  sideActions,
  children,
  contentMaxWidth = 'max-w-md',
  logoPosition = 'right',
  contentVerticalAlign = 'start',
  contentPanelClassName = 'rounded-2xl border border-slate-200/80 bg-white px-6 py-6 text-foreground shadow-xl shadow-slate-950/8 sm:px-7 sm:py-7',
  rightLogoContainerClassName = 'relative z-10 mb-8 mt-2 flex w-full flex-col items-center justify-center text-center',
}: TenantAuthShellProps) {
  const hasHeaderContent = Boolean(eyebrow || title || description || panelTitle || panelDescription)
  const resolvedSidePanelLogos = sidePanelLogos?.length
    ? sidePanelLogos
    : welcomeLogos?.length
      ? welcomeLogos
      : [sidePanelLogo ?? organizationLogo ?? welcomeLogo]
  const logoNode = (
    <AuthLogoImage
      sources={resolvedSidePanelLogos}
      alt={`${organizationName} logo`}
      className="max-h-44 w-auto object-contain drop-shadow-xl sm:max-h-52"
      fallback={(
        <div
          className={`flex h-36 w-36 items-center justify-center rounded-[34px] shadow-sm sm:h-44 sm:w-44 ${logoPosition === 'left' ? 'bg-white/15 text-white ring-1 ring-white/25' : 'bg-primary/10 text-primary'}`}
        >
          <Icon className="h-16 w-16" />
        </div>
      )}
    />
  )

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-4 py-8"
      style={buildAuthThemeStyle(primaryColor)}
    >
      <div className="relative grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_22px_60px_rgba(15,23,42,0.12)] lg:min-h-[620px] lg:grid-cols-[1fr_0.95fr]">
        <section className="relative isolate flex min-h-[260px] items-center justify-center overflow-hidden bg-primary px-6 py-10 text-primary-foreground sm:px-10 lg:min-h-full">
          <div aria-hidden className="pointer-events-none absolute -left-36 top-12 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -right-28 bottom-6 h-[24rem] w-[24rem] rounded-full bg-slate-950/10 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-slate-950/10" />
          {logoPosition === 'left' ? (
            <div className="relative z-10 flex flex-col items-center justify-center gap-8 text-center">
              {logoNode}
              {sideActions ? <div>{sideActions}</div> : null}
            </div>
          ) : null}
        </section>

        <section className={`relative isolate flex flex-col items-center overflow-hidden bg-slate-50/70 p-8 sm:p-10 lg:min-h-full ${contentVerticalAlign === 'center' ? 'justify-center' : 'justify-start'}`}>
          {logoPosition === 'right' ? (
            <div className={rightLogoContainerClassName}>
              {logoNode}
            </div>
          ) : null}
          <div className={`relative z-10 w-full ${contentMaxWidth} ${contentVerticalAlign === 'center' ? 'my-auto' : ''}`}>
            {logoPosition === 'right' && sideActions ? <div className="mb-6">{sideActions}</div> : null}
            <div className={contentPanelClassName}>
            {hasHeaderContent ? (
              <div className="mb-5 flex flex-col items-center text-center">
              {eyebrow ? (
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary/70">
                  {eyebrow}
                </p>
              ) : null}
              {title ? (
                <h1 className={eyebrow ? 'mt-2 text-2xl font-black leading-tight tracking-tight text-primary sm:text-3xl' : 'text-2xl font-black leading-tight tracking-tight text-primary sm:text-3xl'}>
                  {title}
                </h1>
              ) : null}
              {description ? <div className="mt-2 text-sm leading-6 text-muted-foreground">{description}</div> : null}
              {panelTitle ? (
                <h2 className="mt-4 text-2xl font-black lowercase tracking-tight text-primary">{panelTitle}</h2>
              ) : null}
              {panelDescription ? <div className="mt-1 text-sm text-muted-foreground">{panelDescription}</div> : null}
              </div>
            ) : null}
            {children}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
